import { Observable } from 'rxjs';
import { Address } from 'src/app/core/models/address.model';
import { Product } from 'src/app/products/models/product.model';
import { PaymentStatus } from './payment-status.enum';
import { TrackShipment } from './track-shipment.model';

// Interfaces for data models
export interface IOrder {
  additional_charges?: number;
  additional_discount?: number;
  billing_address?: Address;
  buyer?: Buyer;
  buyer_branch?: string;
  buyer_company?: string;
  cart?: string;
  cart_offer_savings?: number;
  cashback_points_earned_from_cart?: number;
  cashback_points_earned_from_products?: number;
  cashpoints_invoiced?: string;
  comments?: Comment[];
  created_at?: string;
  created_by?: string;
  delivery_method?: string;
  discount_amount?: number;
  discount_invoiced?: number;
  discount_percentage?: number;
  id?: string;
  is_active?: boolean;
  is_archived?: boolean;
  last_modified_by?: string;
  order_number?: string;
  order_total?: number;
  owner?: string;
  order_items?: OrderItem[];
  order_date?: string;
  original_order_number?: string;
  offer_applied?: string;
  payment_detail?: PaymentDetail;
  payment_method?: PaymentMethod;
  payment_terms?: PaymentTerms;
  product_offer_savings?: number;
  payment_status?: PaymentStatus;
  quotation?: string;
  reference_number?: string;
  seller_company?: string;
  subtotal?: number;
  shipping_charges?: number;
  shipping_method?: ShippingMethod;
  shipping_address?: Address;
  shipment_date?: string;
  sales_person?: SalesPerson;
  status?: OrderStatus;
  tax?: string;
  other_taxes?: OtherTaxes[];
  total_items?: number;
  total_savings?: number;
  user?: UserPersonalDetails;
  updated_at?: string;
  invoices?: Invoice[];
  shipments?: Shipment[];
  gst_amount?: number;
  can_be_invoiced?: boolean;
  can_be_shipped?: boolean;
  can_retry?: boolean;
  transaction_detail?: TransactionDetail;
  product_offer_discount?: number;
  cart_offer_discount?: number;
  total_cashback_points?: number;
  cash_points_used?: number;
  amount_due?: number;
  payment_method_applicable?: {
    is_minimum_cart_total_valid: boolean;
    additional_amount_required: number;
  };
}

export interface IOrderItem {
  id?: string;
  product?: Product;
  quantity?: number;
  unit_price?: number;
  total_price?: number;
  discount_amount?: number;
  discount_percentage?: number;
  tax_amount?: number;
  tax_percentage?: number;
  created_at?: string;
  updated_at?: string;
}

export interface IOfferDetails {
  id?: string;
  name?: string;
  description?: string;
  discount_type?: string;
  discount_value?: number;
  minimum_order_value?: number;
  maximum_discount?: number;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface IGST {
  id?: string;
  name?: string;
  percentage?: number;
  amount?: number;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface IShippingMethod {
  id?: string;
  name?: string;
  description?: string;
  delivery_time?: string;
  cost?: number;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ITransactionDetail {
  id?: string;
  transaction_id?: string;
  amount?: number;
  currency?: string;
  status?: string;
  payment_method?: string;
  created_at?: string;
  updated_at?: string;
}

export interface IRazorpayTransactionDetail {
  id?: string;
  razorpay_payment_id?: string;
  razorpay_order_id?: string;
  razorpay_signature?: string;
  amount?: number;
  currency?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
}

export interface IUserPersonalDetails {
  id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  created_at?: string;
  updated_at?: string;
}

export interface IPaymentDetail {
  id?: string;
  amount?: number;
  currency?: string;
  payment_method?: string;
  status?: string;
  transaction_id?: string;
  created_at?: string;
  updated_at?: string;
}

export interface PaymentTerms {
  id: string;
  name: string;
  description?: string;
  days: number;
  company: string;
}

export interface SalesPerson {
  id: string;
  name: string;
  email: string;
  phone?: string;
  company: string;
}

export interface Invoice {
  id: string;
  invoice_number: string;
  amount: number;
  status: string;
  created_at: string;
}

export interface OtherTaxes {
  id: string;
  name: string;
  amount: number;
  percentage: number;
  type: string;
}

export interface ProductAttributes {
  [key: string]: string | number | boolean;
}

export interface Taxes {
  id: string;
  name: string;
  amount: number;
  percentage: number;
  type: string;
}

export interface SalesPersonDetails {
  id: string;
  name: string;
  email: string;
  phone?: string;
}

export interface PaymentTermsDetails {
  id: string;
  name: string;
  description?: string;
  days: number;
}

export interface ProductDisplayInfo {
  display_name?: string;
  credit_limit?: number;
}

export interface ContactPerson {
  contact_first_name?: string;
  contact_last_name?: string;
  contact_email?: string;
  contact_mobile_number?: string;
}

export interface OrderDescription {
  description?: string;
}

export class Order {
  constructor(
    public additional_charges?: number,
    public additional_discount?: number,
    public billing_address?: Address,
    public buyer?: Buyer,
    public buyer_branch?: string,
    public buyer_company?: string,
    public cart?: string,
    public cart_offer_savings?: number,
    public cashback_points_earned_from_cart?: number,
    public cashback_points_earned_from_products?: number,
    public cashpoints_invoiced?: string,
    public comments?: Comment[],
    public created_at?: string,
    public created_by?: string,
    public delivery_method?: string,
    public discount_amount?: number,
    public discount_invoiced?: number,
    public discount_percentage?: number,
    public id?: string,
    public is_active?: boolean,
    public is_archived?: boolean,
    public last_modified_by?: string,
    public order_number?: string,
    public order_total?: number,
    public owner?: string,
    public order_items?: OrderItem[],
    public order_date?: string,
    public original_order_number?: string,
    public offer_applied?: string,
    public payment_detail?: PaymentDetail,
    public payment_method?: PaymentMethod,
    public payment_terms?: PaymentTerms,
    public product_offer_savings?: number,
    public payment_status?: PaymentStatus,
    public quotation?: string,
    public reference_number?: string,
    public seller_company?: string,
    public subtotal?: number,
    public shipping_charges?: number,
    public shipping_method?: ShippingMethod,
    public shipping_address?: Address,
    public shipment_date?: string,
    public sales_person?: SalesPerson,
    public status?: OrderStatus,
    public tax?: string,
    public other_taxes?: OtherTaxes[],
    public total_items?: number,
    public total_savings?: number,
    public user?: UserPersonalDetails,
    public updated_at?: string,
    public invoices?: Invoice[],
    public shipments?: Shipment[],
    public gst_amount?: number,
    public can_be_invoiced?: boolean,
    public can_be_shipped?: boolean,
    public can_retry?: boolean,
    public transaction_detail?: TransactionDetail,
    public product_offer_discount?: number,
    public cart_offer_discount?: number,
    public total_cashback_points?: number,
    public cash_points_used?: number,
    public amount_due?: number,
    public amount?: number,
    public payment_method_applicable?: {
      is_minimum_cart_total_valid: boolean;
      additional_amount_required: number;
    }
  ) { }
}
interface PaymentMethod {
  id: string;
  name: string;
  description: string;
  company: string;
  is_active: boolean;
  created_at: Date;
  updated_at: Date;
  gateway_details: GatewayDetails;
  gateway_type: string;
  sales_channel: string[];
}

export enum RazorpayCheckoutType {
  Aggregator = 'aggregator',
  Embedded = 'embedded'
}

interface GatewayDetails {
  merchant_id: string;
  merchant_key: string;
  checkout_type: RazorpayCheckoutType;
}

export enum OrderStatus {
  NEW = 'new',
  CONFIRMED = 'confirmed',
  PROCESSING = 'processing',
  COMPLETE = 'complete',
  CANCELLED = 'cancelled',
  ONHOLD = 'on_hold',
  HASNEWVERSION = 'has_new_version'
}

export const OrderStatuses = [
  { value: '', viewValue: 'All Orders' },
  { value: 'new', viewValue: 'New' },
  { value: 'confirmed', viewValue: 'Confirmed' },
  { value: 'processing', viewValue: 'Processing' },
  { value: 'complete', viewValue: 'Complete' },
  { value: 'cancelled', viewValue: 'Cancelled' },
  { value: 'on_hold', viewValue: 'On Hold' }
  /* { value: 'has_new_version', viewValue: 'Has New Version' } */
];

export enum OrderStatusViewLabels {
  NEW = 'New',
  CONFIRMED = 'Confirmed',
  PROCESSING = 'Processing',
  COMPLETE = 'Complete',
  CANCELLED = 'Cancelled',
  ONHOLD = 'On Hold'
}

export class OrderItem {
  constructor(
    public cashback_points?: string,
    public effective_price_after_cart_offer_split?: number,
    public price_for_user?: number,
    public created_at?: string,
    public created_by?: string,
    public discount_amount?: string,
    public discount_percentage?: string,
    public effective_price_before_offer?: string,
    public gst?: GST,
    public id?: string,
    public is_cart_offer_item?: string,
    public is_product_offer_item?: boolean,
    public offer_applied?: OfferDetails,
    public product_discount_offer?: OfferDetails,
    public last_modified_by?: string,
    public order?: string,
    public owner?: string,
    public price?: number,
    public product?: Product,
    public product_attributes?: string,
    public quantity?: number,
    public quantity_cancelled?: number,
    public quantity_invoiced?: number,
    public quantity_returned?: number,
    public quantity_shipped?: number,
    public tax?: string,
    public total?: string,
    public updated_at?: string,
    public total_product_discount?: string,
    public effective_price?: number,
    public product_total_cash_points?: number,
    public is_payment_method_applicable?: boolean,
    public is_bidding_offer_item?: boolean,
    public product_offer_discounted_amount?: number,
  ) { }
}

export class OfferDetails {
  constructor(
    public id: string,
    public name: string,
    public description: string,
    public offer_type: string
  ) { }
}


export class GST {
  constructor(
    public cgst?: number,
    public sgst?: number,
    public gst_rate?: number,
    public gst_amount?: number,
    public hsn_number?: string,
    public igst?: number
  ) { }
}
export enum DiscountType {
  flat = 'flat',
  percentage = 'percentage',
  CashPoints = 'cash_points'
}

export enum ShippingMethodName {
  SelfPickup = 'Self Pickup',
  Shiprocket = 'Shiprocket',
  HandDelivered = 'Hand Delivered',
  Courier = 'Courier'
}

export class ShippingMethod {
  constructor(
    public created_at: string,
    public description: string,
    public id: string,
    public is_active: true,
    public name: string,
    public updated_at: string,
    public shipping_method_type: string
  ) { }
}

export class TransactionDetail {
  constructor(
    public merchant_id?: string,
    public merchant_name?: string,
    public order_id?: string,
    public token?: string,
    public token_type?: string,
    public transaction_amount?: string,
    public plural_payment_url?: string,
    public order_payment_id?: string,
    public payment_links?: { web: string }
  ) { }
}
export class RazorpayTransactionDetail {
  constructor(
    public id?: string,
    public amount?: number,
    public amount_due?: number,
    public amount_paid?: number,
    public rpay_key_id?: string,
    public order_payment_id?: string
  ) { }
}

export class UserPersonalDetails {
  constructor(
    public country_code?: string,
    public email?: string,
    public first_name?: string,
    public id?: string,
    public last_name?: string,
    public mobile_number?: string
  ) { }
}
export class PaymentDetail {
  constructor(
    public name: string,
    public bank_name: string,
    public gateway_name: string,
    public transaction_date: string,
    public transaction_id: string,
    public transaction_total: string
  ) { }
}

export interface Shipment {
  id: string;
  buyer: Buyer;
  shipment_items: ShipmentItem[];
  invoice: Invoice;
  created_at: Date;
  updated_at: Date;
  owner: string;
  shipment_number: string;
  reference_number?: string;
  courier_name?: string;
  tracking_number?: string;
  tracking_link?: string;
  shipment_date: string;
  expected_delivery_date: string;
  delivery_date?: string;
  comments?: string;
  weight?: number;
  dimensions?: string;
  carrier?: string;
  courier_id?: string;
  shipment_response_details?: Record<string, unknown>;
  third_party_shipment_id?: string;
  third_party_label_link?: string;
  third_party_manifest_link?: string;
  third_party_ndr_details?: Record<string, unknown>;
  delivery_challan?: Record<string, unknown>;
  sales_return?: Record<string, unknown>;
  outgoing_shipment?: Record<string, unknown>;
  status?: string;
  shipping_method?: { name: string };
  tracking$: Observable<number | null>;
  is_returned?: boolean;
}

export interface ShipmentItem {
  id: string;
  product: Product;
  created_at: Date;
  updated_at: Date;
  owner: string;
  mrp: number;
  quantity: number;
  price: number;
  total: number;
  order_item: string;
  delivery_challan_item?: any;
  is_product_offer_item: boolean;
  is_cart_offer_item: boolean;
  gst?: any;
  product_attributes: any;
  created_by: string;
  last_modified_by: string;
  shipment: string;
  is_bidding_offer_item: boolean;
}

export interface OtherTaxes {
  taxes: Taxes;
  total_tax: number;
}

export interface Taxes {
  tax_value_taxes: TaxValueTaxes;
  billed_value_taxes: BilledValueTaxes;
}

export interface TaxValueTaxes {
  taxes: any[];
  total: number;
}
export interface BilledValueTaxes {
  taxes: any[];
  total: number;
}

export interface TrackingForShipRocket {
  details: {
    trackingDetails: TrackShipment;
    order: Order;
  };
}

export interface Buyer {
  id: string;
  seller_company: string;
  buyer_company: BuyerCompany;
  buyer_type: string;
  sales_person: any[];
  buyer_groups: BuyerGroup[];
  payment_terms?: any;
  contact_person: ContactPerson[];
  display_name?: any;
  credit_limit?: any;
  created_at: Date;
  updated_at: Date;
  outstanding_amount: number;
  credit_remaining: number;
}

export interface BuyerCompany {
  id: string;
  name: string;
  contact_first_name?: any;
  contact_last_name?: any;
  contact_email?: any;
  contact_mobile_number?: any;
  account_id: string;
  verified_at: Date;
  is_active: boolean;
}

export interface BuyerGroup {
  id: string;
  name: string;
  company: string;
  is_active: boolean;
  description?: any;
  created_at: Date;
  updated_at: Date;
}

export interface ContactPerson {
  email: string;
  last_name: string;
  first_name: string;
  mobile_number: string;
}

export interface AvailableOffer {
  id: string;
  name: string;
  offer_type: string;
  position: number;
  offer_amount: number;
  maximum_offer_amount_per_transaction: number;
  times_used: number;
  maximum_offer_amount_per_buyer: number;
  minimum_cart_total: number;
  minimum_cart_items: number;
  is_active: boolean;
  coupon_code: string;
  how_to_handle_product_offers: string;
  is_coupon_required: boolean;
  description: string;
  maximum_frequency_per_buyer: number;
}

export enum PartialPaymentValueType {
  Fixed = 'fixed',
  Percentage = 'percentage'
}

// Utility functions to create default objects
export function createDefaultOrder(): Order {
  return new Order();
}

export function createDefaultTransactionDetail(): TransactionDetail {
  return new TransactionDetail();
}

export function createDefaultRazorpayTransactionDetail(): RazorpayTransactionDetail {
  return new RazorpayTransactionDetail();
}

export function createDefaultUserPersonalDetails(): UserPersonalDetails {
  return new UserPersonalDetails();
}